import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { callAPI } from "../../../helper/apiUtils";
import { REFRESH_TODO_LIST, SET_PENDING_PROJECT_TASK } from "../../../store";
import CockpitBriefingText, { prioritiesFromEmail } from "../../Cockpit/components/CockpitBriefingText";
import { Card, EmptyState, Skeleton } from "../../Cockpit/components/CockpitPrimitives";
import { cockpitLocale, fill } from "../../Cockpit/cockpitFormat";
import { fetchBookmarkedTodos, fetchReportTasks, syncPriorityBookmarks } from "../../Cockpit/priorityBookmarks";
import { plainTodoText, splitTitle, taskBucket } from "../../Feeds/todoProModel";
import "../../Cockpit/Cockpit.css";
import "./RoomOverview.css";

/**
 * "Übersicht" of a project (Kjuup.io): "Heute wichtig", the top five
 * priorities of the project's latest report as cards in a row that scrolls
 * sideways (no summary text), then the project's open tasks in two columns:
 * "Fristen & Aufgaben" on the left, "Termine" on the right. The server writes that report
 * at night for projects with new messages, emails, Teams messages or tasks
 * (own AI, or Mistral when the user chose it in the settings); this page only
 * reads it. Its priorities become the project's tasks, as on the cockpit, and
 * are listed in the project's "Aufgaben" under the cockpit's two headers.
 */
const reportId = (r) => r && (r.id || r._id || r.messageId);
const reportTime = (r) => {
  const t = Date.parse((r && (r.receivedAt || r.date)) || "");
  return Number.isFinite(t) ? t : 0;
};

function asText(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (value && value.type === "Buffer" && Array.isArray(value.data)) {
    try {
      return new TextDecoder("utf-8").decode(Uint8Array.from(value.data));
    } catch (_) {
      return "";
    }
  }
  return "";
}

const hasBody = (r) => !!r && !!(asText(r.bodyHtml) || asText(r.html) || asText(r.bodyText) || asText(r.body)).trim();

function listRows(data) {
  if (!data) return [];
  const rows = Array.isArray(data.messages) ? data.messages : Array.isArray(data.data) ? data.data : [];
  return rows.filter((m) => {
    if (!m || !reportId(m)) return false;
    const labels = Array.isArray(m.labels) ? m.labels : [];
    return !labels.some((l) => String(l).toUpperCase() === "TRASH");
  });
}

function singleMessage(data) {
  if (!data || typeof data !== "object") return null;
  if (data.message && typeof data.message === "object") return data.message;
  if (data.data && typeof data.data === "object" && (data.data.id != null || data.data.subject !== undefined)) return data.data;
  if (data.id != null || data.subject !== undefined) return data;
  return null;
}

function isBookmarked(todo) {
  const v = todo && todo.isBookmarked;
  if (v === true || v === 1 || v === "1") return true;
  if (v && typeof v === "object" && Array.isArray(v.data)) return Number(v.data[0]) === 1;
  return false;
}

// The room's latest report with its body (the folder list has none).
async function loadLatestReport(roomId) {
  const res = await callAPI(
    `/email/messages?folder=BRIEFING&buildingId=${encodeURIComponent(roomId)}&page=1&limit=5`,
    {},
    "GET"
  );
  const latest = listRows(res && res.data).sort((a, b) => reportTime(b) - reportTime(a))[0] || null;
  if (!latest || hasBody(latest)) return latest;
  const params = {};
  const accountId = latest.emailAccountId || latest.emailAccount;
  if (accountId) params.accountId = accountId;
  const full = await callAPI(`/email/messages/${encodeURIComponent(String(reportId(latest)))}`, params, "GET").catch(() => null);
  const message = singleMessage(full && full.data);
  return message && hasBody(message) ? { ...latest, ...message } : latest;
}

async function loadRoomTodos(roomId) {
  const res = await callAPI("/todo/userTodo", {}, "POST", { search: "", limit: 200, page: 1, buildingId: roomId });
  return Array.isArray(res && res.data && res.data.data) ? res.data.data : [];
}

/**
 * The report's priorities become the project's tasks, as the cockpit does with
 * the report it shows: the same sync and the same record per report, so the
 * two never create a task twice. Every row of a project report belongs to this
 * project. `changed` when tasks were created or moved into the project;
 * `links` ([{ id, text, roomId }]) name the task behind each priority — the
 * report's record first, then the project's tasks by text (one scheduled by
 * hand is not recorded) — so "Aufgabe öffnen" opens that task.
 */
async function syncReportTasks(report, roomId) {
  const priorities = prioritiesFromEmail(report).map((row) => (row.roomId ? row : { ...row, roomId }));
  if (!priorities.length) return { changed: false, links: [] };
  const [loaded, sorted, roomTodos] = await Promise.all([
    fetchBookmarkedTodos(),
    fetchReportTasks([]).catch(() => []),
    loadRoomTodos(roomId).catch(() => []),
  ]);
  const { created, repaired, todos } = await syncPriorityBookmarks({
    report,
    priorities,
    rooms: [],
    bookmarks: loaded.items.concat(sorted),
    dated: roomTodos.filter((todo) => !isBookmarked(todo)),
  });
  const links = (todos || [])
    .concat(roomTodos.map((todo) => ({ id: todo.todoId, text: todo.description })))
    .filter((link) => link && link.id != null && link.text)
    .map((link) => ({ id: String(link.id), text: link.text, roomId: String(roomId) }));
  return { changed: !!(created || repaired), links };
}

// A task's day: a dated task's own date, a report task the day its report named.
function dueOf(todo) {
  if (!todo) return null;
  if (isBookmarked(todo)) {
    const day = String(todo.bookmarkDeadline || "");
    return /^\d{4}-\d{2}-\d{2}$/.test(day) ? moment(day, "YYYY-MM-DD") : null;
  }
  const m = todo.createdAt ? moment(todo.createdAt) : null;
  return m && m.isValid() ? m : null;
}

// Earliest first, those without a day last.
function byDue(a, b) {
  const da = dueOf(a);
  const db = dueOf(b);
  if (!da || !db) return Number(!da) - Number(!db);
  return da.valueOf() - db.valueOf();
}

// Open tasks of the room: appointments apart, the rest under "Fristen & Aufgaben".
async function loadOpenTasks(roomId, currentUserId) {
  const rows = await loadRoomTodos(roomId);
  const open = rows
    .filter((todo) => taskBucket(todo, { currentUserId, bookmarked: isBookmarked(todo) }) !== "done")
    .sort(byDue);
  return {
    tasks: open.filter((todo) => todo.bookmarkCategory !== "appointment"),
    appointments: open.filter((todo) => todo.bookmarkCategory === "appointment"),
  };
}

// One task of a column: its day, its title, and when it is due.
function OverviewTaskRow({ todo, appointment, strings, language, onOpen }) {
  const due = dueOf(todo);
  const locale = cockpitLocale(language);
  const overdue = !!due && due.isBefore(moment(), "day");
  const { title } = splitTitle(plainTodoText(todo.description));
  const timed = appointment && due && !isBookmarked(todo);
  const meta = [
    overdue ? strings.cockpit_chip_overdue || "Überfällig" : null,
    due ? due.clone().locale(locale).format("dddd, D. MMMM") : strings.cockpit_todo_no_date || "Ohne Datum",
    timed ? fill(strings.cockpit_todo_at || "{{time}} Uhr", { time: due.format("HH:mm") }) : null,
  ].filter(Boolean).join(" · ");
  return (
    <li>
      <button type="button" className={`room-overview-task${overdue ? " is-overdue" : ""}`} onClick={() => onOpen(todo.todoId)}>
        <span className="room-overview-task-day" aria-hidden="true">
          <span className="room-overview-task-weekday">{due ? due.clone().locale(locale).format("dd") : "–"}</span>
          <span className="room-overview-task-date">{due ? due.format("DD") : ""}</span>
        </span>
        <span className="room-overview-task-body">
          <span className="room-overview-task-title">{title || "—"}</span>
          <span className="room-overview-task-meta">{meta}</span>
        </span>
      </button>
    </li>
  );
}

// A column of the Übersicht: "Fristen & Aufgaben" or "Termine".
function OverviewTaskColumn({ title, items, appointment, loading, emptyText, strings, language, onOpen, onOpenAll }) {
  return (
    <Card className="room-overview-column">
      <div className="room-overview-column-head">
        <h4 className="room-overview-column-title">
          {title}
          {!loading && items.length ? <span className="room-overview-column-count">{items.length}</span> : null}
        </h4>
        {onOpenAll ? (
          <button type="button" className="kj-btn kj-btn--ghost kj-btn--sm room-overview-column-all" onClick={onOpenAll}>
            {strings.room_overview_all_tasks || "Alle"}
          </button>
        ) : null}
      </div>
      {loading ? (
        <div className="room-overview-skeleton" aria-hidden="true">
          <Skeleton height={14} width="80%" />
          <Skeleton height={14} width="65%" />
          <Skeleton height={14} width="72%" />
        </div>
      ) : items.length ? (
        <ul className="room-overview-task-list">
          {items.map((todo) => (
            <OverviewTaskRow
              key={String(todo.todoId)}
              todo={todo}
              appointment={appointment}
              strings={strings}
              language={language}
              onOpen={onOpen}
            />
          ))}
        </ul>
      ) : (
        <p className="room-overview-column-empty">{emptyText}</p>
      )}
    </Card>
  );
}

export default function RoomOverview({ roomId, strings, language, active = true, onOpenTasks }) {
  const dispatch = useDispatch();
  const currentUserId = useSelector((s) => s.user && s.user.id);
  const todoRefresh = useSelector((s) => s.todoListRefreshTrigger);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(true);
  const [openTasks, setOpenTasks] = useState(null);
  const [taskLinks, setTaskLinks] = useState([]);
  const loadRef = useRef(0);

  const loadReport = useCallback(async () => {
    const token = ++loadRef.current;
    try {
      const next = await loadLatestReport(roomId);
      if (token === loadRef.current) setReport(next);
    } catch (_) {
      if (token === loadRef.current) setReport(null);
    } finally {
      if (token === loadRef.current) setReportLoading(false);
    }
  }, [roomId]);

  const tasksRef = useRef(0);
  const loadTasks = useCallback(async () => {
    const token = ++tasksRef.current;
    try {
      const next = await loadOpenTasks(roomId, currentUserId);
      if (token === tasksRef.current) setOpenTasks(next);
    } catch (_) {
      if (token === tasksRef.current) setOpenTasks((prev) => prev || { tasks: [], appointments: [] });
    }
  }, [roomId, currentUserId]);

  useEffect(() => {
    if (!active || !roomId) return;
    loadReport();
  }, [active, roomId, loadReport]);

  // Again after a task changed anywhere (done, moved, new).
  useEffect(() => {
    if (!active || !roomId) return;
    loadTasks();
  }, [active, roomId, loadTasks, todoRefresh]);

  // The report's priorities land in the project's task list ("Aufgaben").
  const reportKey = report && hasBody(report) ? String(reportId(report)) : null;
  useEffect(() => {
    if (!reportKey || !roomId) return undefined;
    let cancelled = false;
    setTaskLinks([]);
    syncReportTasks(report, roomId)
      .then(({ changed, links }) => {
        if (cancelled) return;
        setTaskLinks(links);
        // The refresh reloads the two columns too.
        if (changed) dispatch({ type: REFRESH_TODO_LIST });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportKey, roomId]);

  // "Aufgabe öffnen": the task in this project's Aufgaben, without leaving the
  // project (ProjectDetail shows Aufgaben, its task pane opens the task); a
  // priority without a task just shows Aufgaben.
  const openTask = (todoId) => {
    if (todoId == null) {
      if (onOpenTasks) onOpenTasks();
      return;
    }
    dispatch({ type: SET_PENDING_PROJECT_TASK, payload: { roomId: String(roomId), todoId: String(todoId), at: Date.now() } });
  };

  // Only the report's top five, each as a card with its detail.
  const statusTitle = strings.room_overview_today_title || "Heute wichtig";
  const asOf = report ? reportTime(report) : 0;
  const asOfLabel = asOf ? (
    <span className="room-overview-asof">
      {fill(strings.room_overview_as_of || "Stand: {{time}}", {
        time: moment(asOf).locale(String(language || "de").split("-")[0]).calendar(null, {
          sameElse: "DD.MM.YYYY, HH:mm",
        }),
      })}
    </span>
  ) : null;

  let status;
  if (reportLoading && !report) {
    status = (
      <div className="room-overview-skeleton" aria-hidden="true">
        <Skeleton height={16} width="40%" />
        <Skeleton height={12} width="95%" />
        <Skeleton height={12} width="88%" />
        <Skeleton height={12} width="60%" />
      </div>
    );
  } else if (report && hasBody(report)) {
    status = (
      <div className="cockpit-report-reveal" key={String(reportId(report))}>
        <CockpitBriefingText
          email={report}
          strings={strings}
          summaryOnly
          prioritiesOnly
          priorityCards
          hideProject
          fazitTitle={statusTitle}
          toolbar={asOfLabel}
          taskLinks={taskLinks}
          onOpenTask={openTask}
        />
      </div>
    );
  } else {
    // Same frame as the report's summary, so the title keeps its padding and size.
    status = (
      <article className="cockpit-briefing">
        <section className="cockpit-briefing-section">
          <div className="cockpit-briefing-head">
            <h4 className="cockpit-briefing-fazit-title">{statusTitle}</h4>
          </div>
          <EmptyState
            icon="summarize"
            title={strings.room_overview_empty || "Noch kein Stand für dieses Projekt"}
            hint={strings.room_overview_empty_hint || "Der Stand wird jede Nacht für Projekte mit neuen Nachrichten, E-Mails oder Aufgaben geschrieben."}
          />
        </section>
      </article>
    );
  }

  return (
    <div className="cockpit room-overview">
      <Card className="cockpit-report room-overview-status">{status}</Card>
      <div className="room-overview-columns">
        <OverviewTaskColumn
          title={strings.cockpit_group_tasks || "Fristen & Aufgaben"}
          items={openTasks ? openTasks.tasks : []}
          loading={!openTasks}
          emptyText={strings.room_overview_tasks_empty || "Keine offenen Fristen oder Aufgaben"}
          strings={strings}
          language={language}
          onOpen={openTask}
          onOpenAll={onOpenTasks}
        />
        <OverviewTaskColumn
          title={strings.cockpit_group_appointments || "Termine"}
          items={openTasks ? openTasks.appointments : []}
          appointment
          loading={!openTasks}
          emptyText={strings.room_overview_appointments_empty || "Keine anstehenden Termine"}
          strings={strings}
          language={language}
          onOpen={openTask}
          onOpenAll={onOpenTasks}
        />
      </div>
    </div>
  );
}
